import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, Clock, RefreshCw, Trophy, Zap } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { getApiErrorMessage } from '../../utils/apiError';
import useReducedMotion from '../../hooks/useReducedMotion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_TIME = 60; // seconds
const POINTS_CORRECT = 10;
const POINTS_WRONG = 5;


/**
 * Extracts time-race questions from various backend shapes.
 * @param {Object} lesson
 * @returns {Array|null}
 */
function getQuestions(lesson) {
  const raw =
    lesson?.content?.questions ||
    lesson?.questions ||
    null;

  if (raw && Array.isArray(raw) && raw.length > 0) {
    return raw.map((q, i) => ({
      id: q.id || i + 1,
      question: q.question || q.text || '',
      options: Array.isArray(q.options) ? q.options : [],
      correctIndex: q.correctIndex ?? q.correct_index ?? 0,
    }));
  }
  return null;
}

/**
 * TimeRaceLesson — Zamana karşı quiz alıştırması (lesson_type === 'time_race').
 *
 * Her doğru cevap +10 puan, yanlış -5 puan.
 * Sayaç bitince veya tüm sorular bitince oyun sona erer.
 *
 * Backend content shape (Faz 4e ile gelecek):
 *   lesson.content.questions: [{ question, options, correctIndex }]
 *   lesson.content.time_limit: number (seconds, default 60)
 *
 * @param {{ lesson: Object, courseId: string, onComplete: Function, completedLessonIds: string[] }} props
 */
export default function TimeRaceLesson({ lesson, courseId, onComplete, completedLessonIds = [] }) {
  const { t, lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();
  const reduced = useReducedMotion();

  const lessonId = lesson?.lesson_id;
  const isCompleted = completedLessonIds.includes(lessonId);

  const rawQs = getQuestions(lesson);
  const hasContent = rawQs !== null && rawQs.length > 0;
  // Stabilize: `|| []` creates a new array ref on every render when rawQs is
  // null, causing handleAnswer/finishGame useCallback to re-create needlessly.
  const questions = useMemo(() => hasContent ? rawQs : [], [hasContent, rawQs]);
  const total = questions.length;
  const timeLimit = lesson?.content?.time_limit || lesson?.time_limit || DEFAULT_TIME;

  const [phase, setPhase] = useState('idle'); // 'idle' | 'playing' | 'finished'
  const [qIndex, setQIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(timeLimit);
  const [score, setScore] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [wrongCount, setWrongCount] = useState(0);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [timeUsed, setTimeUsed] = useState(0);

  const timerRef = useRef(null);

  // Timer logic
  useEffect(() => {
    if (phase !== 'playing') return;

    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setTimeUsed(_t => timeLimit - 0); // will recalc below
          setPhase('finished');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [phase, timeLimit]);

  const handleStart = () => {
    setPhase('playing');
    setQIndex(0);
    setTimeLeft(timeLimit);
    setScore(0);
    setCorrectCount(0);
    setWrongCount(0);
    setSelectedIdx(null);
    setAnswered(false);
    setTimeUsed(0);
  };

  const finishGame = useCallback((finalScore, correct, wrong, tLeft) => {
    clearInterval(timerRef.current);
    setTimeUsed(timeLimit - tLeft);
    setPhase('finished');
  }, [timeLimit]);

  const handleAnswer = useCallback((idx) => {
    if (answered || phase !== 'playing') return;

    const current = questions[qIndex];
    const isCorrect = idx === current.correctIndex;

    setSelectedIdx(idx);
    setAnswered(true);

    const newScore = isCorrect ? score + POINTS_CORRECT : Math.max(0, score - POINTS_WRONG);
    const newCorrect = isCorrect ? correctCount + 1 : correctCount;
    const newWrong = isCorrect ? wrongCount : wrongCount + 1;

    setScore(newScore);
    if (isCorrect) setCorrectCount(newCorrect);
    else setWrongCount(newWrong);

    // Auto-advance after 1 second
    setTimeout(() => {
      const nextIdx = qIndex + 1;
      if (nextIdx >= total) {
        finishGame(newScore, newCorrect, newWrong, timeLeft);
      } else {
        setQIndex(nextIdx);
        setSelectedIdx(null);
        setAnswered(false);
      }
    }, 1000);
  }, [answered, phase, questions, qIndex, score, correctCount, wrongCount, total, finishGame, timeLeft]);

  const handleComplete = async () => {
    if (completing) return;
    setCompleting(true);
    const maxScore = total * POINTS_CORRECT;
    const pct = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
    try {
      await axios.post(
        `${API}/enrollments/complete-lesson`,
        { course_id: courseId, lesson_id: lessonId, quiz_score: pct },
        { headers: authHeaders() }
      );
      toast.success(lang === 'tr' ? 'Ders tamamlandı!' : 'Lesson completed!');
      if (onComplete) onComplete();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setCompleting(false);
    }
  };

  // Derived
  const timerPct = (timeLeft / timeLimit) * 100;
  const timerColor = timeLeft > 20 ? 'bg-emerald-500' : timeLeft > 10 ? 'bg-amber-500' : 'bg-rose-500';

  if (isCompleted) {
    return (
      <div className="rounded-2xl border-2 border-green-200 bg-green-50 p-6 mb-6 text-center" data-testid="timerace-already-completed">
        <CheckCircle size={32} className="mx-auto text-green-600 mb-2" />
        <p className="font-bold text-sm text-green-700">
          {lang === 'tr' ? 'Bu ders tamamlandı' : 'This lesson is completed'}
        </p>
      </div>
    );
  }

  if (!hasContent) {
    return (
      <div className="text-center py-12" data-testid="timerace-empty-state">
        <Clock size={40} className="mx-auto text-gray-300 mb-4" />
        <p className="text-gray-500 font-semibold mb-4">
          {lang === 'tr' ? 'Bu derste henüz zamanlı quiz içeriği tanımlanmamış.' : 'No time race content has been defined for this lesson yet.'}
        </p>
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={handleComplete}
          disabled={completing}
          className="px-6 py-3 rounded-xl border-2 border-emerald-500 bg-emerald-500 text-white font-bold shadow-[0_4px_0_#059669] active:shadow-none active:translate-y-1 transition-all disabled:opacity-50"
          data-testid="timerace-empty-complete-btn"
        >
          {completing ? '...' : t('common.completeLesson')}
        </motion.button>
      </div>
    );
  }

  return (
    <div data-testid="time-race-lesson">
      <AnimatePresence mode="wait">
        {/* IDLE screen */}
        {phase === 'idle' && (
          <motion.div
            key="timerace-idle"
            initial={reduced ? {} : { opacity: 0, y: 20 }}
            animate={reduced ? {} : { opacity: 1, y: 0 }}
            className="rounded-2xl border-2 border-cyan-200 bg-gradient-to-br from-cyan-50 to-blue-50 p-8 text-center"
            data-testid="timerace-idle"
          >
            <div className="w-20 h-20 rounded-full bg-cyan-500 flex items-center justify-center mx-auto mb-4">
              <Clock size={40} className="text-white" />
            </div>
            <h2 className="font-display text-3xl font-bold text-cyan-700 mb-2">
              {t('timeRaceLesson.title')}
            </h2>
            <p className="text-gray-600 font-semibold mb-2">
              {total} {t('timeRaceLesson.questions')} · {timeLimit}s
            </p>
            <div className="flex justify-center gap-6 text-sm font-bold mb-6">
              <span className="flex items-center gap-1 text-emerald-600">
                <Zap size={14} /> +{POINTS_CORRECT} {t('timeRaceLesson.perCorrect')}
              </span>
              <span className="flex items-center gap-1 text-rose-500">
                <XCircle size={14} /> -{POINTS_WRONG} {t('timeRaceLesson.perWrong')}
              </span>
            </div>
            <motion.button
              whileHover={reduced ? {} : { scale: 1.04 }}
              whileTap={reduced ? {} : { scale: 0.96 }}
              onClick={handleStart}
              className="px-10 py-4 rounded-xl border-2 border-cyan-500 bg-cyan-500 text-white font-bold text-lg shadow-[0_5px_0_#0891b2] active:shadow-none active:translate-y-1.5 transition-all"
              data-testid="timerace-start-btn"
            >
              {t('timeRaceLesson.start')}
            </motion.button>
          </motion.div>
        )}

        {/* PLAYING screen */}
        {phase === 'playing' && (
          <motion.div
            key={`timerace-q-${qIndex}`}
            initial={reduced ? {} : { opacity: 0, x: 30 }}
            animate={reduced ? {} : { opacity: 1, x: 0 }}
            exit={reduced ? {} : { opacity: 0, x: -30 }}
            transition={reduced ? {} : { duration: 0.2 }}
            data-testid="timerace-playing"
          >
            {/* Timer bar */}
            <div className="mb-4">
              <div className="flex justify-between items-center mb-1">
                <span className="flex items-center gap-1.5 font-bold text-sm text-gray-600">
                  <Clock size={16} className={timeLeft <= 10 ? 'text-rose-500 animate-pulse' : 'text-gray-500'} />
                  <span className={`font-display text-2xl font-bold tabular-nums ${timeLeft <= 10 ? 'text-rose-500' : 'text-gray-700'}`}>
                    {timeLeft}s
                  </span>
                </span>
                <div className="flex gap-3 text-xs font-bold">
                  <span className="flex items-center gap-1 text-emerald-600">
                    <Zap size={12} /> {score} {t('timeRaceLesson.points')}
                  </span>
                  <span className="text-gray-400">{qIndex + 1}/{total}</span>
                </div>
              </div>
              <div className="h-3 bg-gray-200 rounded-full overflow-hidden border border-gray-300">
                <motion.div
                  className={`h-full rounded-full transition-colors ${timerColor}`}
                  animate={{ width: `${timerPct}%` }}
                  transition={{ duration: 1, ease: 'linear' }}
                />
              </div>
            </div>

            {/* Question */}
            <div className="rounded-2xl border-2 border-cyan-200 bg-cyan-50 px-6 py-5 mb-4" data-testid="timerace-question">
              <p className="font-display text-xl font-bold text-gray-800">
                {questions[qIndex]?.question}
              </p>
            </div>

            {/* Options */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="timerace-options">
              {questions[qIndex]?.options.map((opt, idx) => {
                const isSelected = selectedIdx === idx;
                const isCorrect = idx === questions[qIndex].correctIndex;
                let btnClass = 'w-full text-left px-4 py-4 rounded-xl border-2 font-bold transition-all text-sm ';

                if (!answered) {
                  btnClass += 'border-gray-300 bg-white text-gray-700 hover:border-cyan-400 hover:bg-cyan-50 shadow-[0_3px_0_#e5e7eb] active:shadow-none active:translate-y-0.5 cursor-pointer';
                } else if (isSelected && isCorrect) {
                  btnClass += 'border-emerald-400 bg-emerald-50 text-emerald-700';
                } else if (isSelected && !isCorrect) {
                  btnClass += 'border-rose-400 bg-rose-50 text-rose-700';
                } else if (!isSelected && isCorrect) {
                  btnClass += 'border-emerald-300 bg-emerald-50 text-emerald-600';
                } else {
                  btnClass += 'border-gray-200 bg-gray-50 text-gray-400 opacity-60';
                }

                return (
                  <motion.button
                    key={idx}
                    whileHover={(!answered && !reduced) ? { scale: 1.02 } : {}}
                    whileTap={(!answered && !reduced) ? { scale: 0.98 } : {}}
                    onClick={() => handleAnswer(idx)}
                    disabled={answered}
                    className={btnClass}
                    data-testid={`timerace-option-${idx}`}
                  >
                    <span className="flex items-center gap-2">
                      {answered && isSelected && (
                        isCorrect
                          ? <CheckCircle size={16} className="text-emerald-500 flex-shrink-0" />
                          : <XCircle size={16} className="text-rose-500 flex-shrink-0" />
                      )}
                      {answered && !isSelected && isCorrect && (
                        <CheckCircle size={16} className="text-emerald-400 flex-shrink-0" />
                      )}
                      {opt}
                    </span>
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* FINISHED screen */}
        {phase === 'finished' && (
          <motion.div
            key="timerace-done"
            initial={reduced ? {} : { opacity: 0, scale: 0.9 }}
            animate={reduced ? {} : { opacity: 1, scale: 1 }}
            className="rounded-2xl border-2 border-cyan-200 bg-gradient-to-br from-cyan-50 to-blue-50 p-8 text-center"
            data-testid="timerace-finished"
          >
            <div className="w-20 h-20 rounded-full bg-amber-400 flex items-center justify-center mx-auto mb-4">
              <Trophy size={40} className="text-white" />
            </div>
            <h2 className="font-display text-3xl font-bold text-cyan-700 mb-4">
              {timeLeft === 0 ? t('timeRaceLesson.timeUp') : t('timeRaceLesson.allDone')}
            </h2>

            {/* Score breakdown */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-3">
                <p className="font-display text-2xl font-bold text-emerald-600">{correctCount}</p>
                <p className="text-xs font-bold text-emerald-500 uppercase">{t('timeRaceLesson.correct')}</p>
              </div>
              <div className="rounded-xl border-2 border-cyan-200 bg-cyan-50 p-3">
                <p className="font-display text-2xl font-bold text-cyan-600">{score}</p>
                <p className="text-xs font-bold text-cyan-500 uppercase">{t('timeRaceLesson.points')}</p>
              </div>
              <div className="rounded-xl border-2 border-gray-200 bg-gray-50 p-3">
                <p className="font-display text-2xl font-bold text-gray-600">{timeUsed || (timeLimit - timeLeft)}s</p>
                <p className="text-xs font-bold text-gray-500 uppercase">{t('timeRaceLesson.timeUsed')}</p>
              </div>
            </div>

            <p className="text-gray-500 font-semibold text-sm mb-6">
              {t('timeRaceLesson.finalScore')}: <strong className="text-cyan-700">{score}</strong> {t('timeRaceLesson.points')}
            </p>

            <div className="flex justify-center gap-4">
              <motion.button
                whileHover={reduced ? {} : { scale: 1.03 }}
                whileTap={reduced ? {} : { scale: 0.97 }}
                onClick={handleComplete}
                disabled={completing}
                className="px-8 py-3 rounded-xl border-2 border-emerald-500 bg-emerald-500 text-white font-bold shadow-[0_4px_0_#059669] active:shadow-none active:translate-y-1 transition-all disabled:opacity-50 flex items-center gap-2"
                data-testid="timerace-complete-btn"
              >
                <CheckCircle size={18} />
                {completing ? '...' : t('common.completeLesson')}
              </motion.button>
              <motion.button
                whileHover={reduced ? {} : { scale: 1.03 }}
                whileTap={reduced ? {} : { scale: 0.97 }}
                onClick={handleStart}
                className="px-6 py-3 rounded-xl border-2 border-gray-300 bg-white font-bold shadow-[0_4px_0_#d1d5db] active:shadow-none active:translate-y-1 transition-all flex items-center gap-2 text-gray-700"
                data-testid="timerace-restart-btn"
              >
                <RefreshCw size={16} /> {t('timeRaceLesson.restart')}
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
